import { useState } from "react";
import { z } from "zod";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, Phone, Mail, MapPin, Linkedin, Instagram, MessageCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import SEOHead from "@/components/SEOHead";
import { useSitePage } from "@/lib/cms";
import { emptyLeadForm, getLeadFormErrorMessage, submitLeadForm } from "@/lib/leadForms";
import { getString, getStringArray } from "@/lib/pageContent";
import { buildBreadcrumb, buildWebPage } from "@/lib/seo/schemas";

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

const countryCodes = [
  { code: "+91", label: "🇮🇳 +91" },
  { code: "+971", label: "🇦🇪 +971" },
  { code: "+1", label: "🇺🇸 +1" },
  { code: "+44", label: "🇬🇧 +44" },
  { code: "+61", label: "🇦🇺 +61" },
  { code: "+65", label: "🇸🇬 +65" },
  { code: "+49", label: "🇩🇪 +49" },
  { code: "+33", label: "🇫🇷 +33" },
  { code: "+81", label: "🇯🇵 +81" },
  { code: "+86", label: "🇨🇳 +86" },
  { code: "+966", label: "🇸🇦 +966" },
  { code: "+974", label: "🇶🇦 +974" },
  { code: "+968", label: "🇴🇲 +968" },
  { code: "+973", label: "🇧🇭 +973" },
  { code: "+965", label: "🇰🇼 +965" },
  { code: "+27", label: "🇿🇦 +27" },
  { code: "+234", label: "🇳🇬 +234" },
  { code: "+254", label: "🇰🇪 +254" },
  { code: "+60", label: "🇲🇾 +60" },
  { code: "+63", label: "🇵🇭 +63" },
  { code: "+82", label: "🇰🇷 +82" },
  { code: "+39", label: "🇮🇹 +39" },
  { code: "+34", label: "🇪🇸 +34" },
  { code: "+7", label: "🇷🇺 +7" },
  { code: "+55", label: "🇧🇷 +55" },
  { code: "+52", label: "🇲🇽 +52" },
  { code: "+92", label: "🇵🇰 +92" },
  { code: "+880", label: "🇧🇩 +880" },
  { code: "+94", label: "🇱🇰 +94" },
  { code: "+977", label: "🇳🇵 +977" },
];

const Contact = () => {
  const { toast } = useToast();
  const { data: page } = useSitePage("contact");
  const content = (page?.content ?? {}) as Record<string, unknown>;
  const [form, setForm] = useState(emptyLeadForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countryCode, setCountryCode] = useState("+91");

  const subjectOptions = getStringArray(content.subjectOptions, [
    "Individual Coaching",
    "Corporate Training",
    "Image Consulting",
    "Campus-to-Corporate Training",
    "Handwriting Analysis",
    "ART Therapy for Stress Relief & Confidence",
    "Something Else",
    "General Inquiry",
  ]);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});
    setIsSubmitting(true);

    try {
      const submissionForm = { ...form, phone: form.phone ? `${countryCode} ${form.phone}` : "" };
      await submitLeadForm(submissionForm, "contact-page");
      toast({ title: "Message Sent!", description: "Thank you! Mahirah will respond within 24 hours." });
      setForm(emptyLeadForm);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errors: Record<string, string> = {};
        error.issues.forEach((issue) => {
          const field = issue.path[0];
          if (field && !errors[field]) errors[field as string] = issue.message;
        });
        setFieldErrors(errors);
      }
      toast({
        title: "Unable to send message",
        description: getLeadFormErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const indiaPhone = getString(content.indiaPhone, "+91 78998 04670");
  const uaePhone = getString(content.uaePhone, "+971 55 283 2143");
  const email = getString(content.email, "mvibeon@gmail.com");
  const address = getString(content.address, "Building 12 Bawabat Al Sharq, Baniyas, Abu Dhabi, United Arab Emirates");
  const linkedinUrl = getString(content.linkedinUrl, "https://www.linkedin.com/company/mvibeon-training-coaching/");
  const instagramUrl = getString(content.instagramUrl, "https://www.instagram.com/mvibe_on?igsh=ZjJmNGZnZXNnYWJr");
  const whatsappIndiaUrl = getString(content.whatsappIndiaUrl, "https://wa.me/917899804670");
  const whatsappUaeUrl = getString(content.whatsappUaeUrl, "https://wa.me/971552832143");

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "MViBE",
    url: "https://mvibeon.com/",
    image: "https://mvibeon.com/og-image.jpg",
    telephone: ["+971 55 283 2143", "+91 78998 04670"],
    email: "mvibeon@gmail.com",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Building 12, Bawabat Al Sharq",
      addressLocality: "Baniyas",
      addressRegion: "Abu Dhabi",
      addressCountry: "AE",
    },
    areaServed: ["AE", "IN"],
    priceRange: "$$",
  };

  return (
    <>
      <SEOHead
        title={page?.seo.title ?? "Contact MVIBE — Book a Discovery Call in Dubai"}
        description={page?.seo.description ?? "Talk to MVIBE about executive coaching or corporate training in Dubai. Book a free discovery call or enquire about programmes for your team."}
        canonical={page?.seo.canonical}
        ogTitle={page?.seo.ogTitle}
        ogDescription={page?.seo.ogDescription}
        ogImage={page?.seo.ogImage}
        type={page?.seo.type ?? "website"}
        schema={page?.seo.schema ?? [
          localBusinessSchema,
          buildWebPage({
            name: "Contact MVIBE",
            description: "Book a free discovery call with MVIBE for executive coaching or corporate soft skills training.",
            path: "/contact",
            type: "ContactPage",
          }),
          buildBreadcrumb([
            { name: "Home", path: "/" },
            { name: "Contact", path: "/contact" },
          ]),
        ]}
      />


      <section className="bg-primary py-14 text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <nav className="mb-4 flex items-center justify-center gap-1.5 text-base text-primary-foreground/60">
            <Link to="/" className="transition-colors hover:text-primary-foreground">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-primary-foreground">Contact Us</span>
          </nav>
          <motion.h1 variants={fadeUp} initial="hidden" animate="visible" transition={{ duration: 0.6 }} className="mx-auto max-w-4xl text-3xl font-heading font-bold md:text-5xl lg:text-6xl">
            {getString(content.heroTitle, "Let's Connect: Start Your Journey to Clarity Within. Success Beyond.")}
          </motion.h1>
          <motion.p variants={fadeUp} initial="hidden" animate="visible" transition={{ duration: 0.6, delay: 0.15 }} className="mx-auto mt-3 max-w-2xl text-lg text-primary-foreground/80">
            {getString(content.heroDescription, "Whether you're an individual seeking coaching or an organisation looking for training solutions — your next breakthrough begins with a conversation.")}
          </motion.p>
        </div>
      </section>

      <section className="bg-background py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <h2 className="mb-1.5 text-2xl font-heading font-bold text-foreground">{getString(content.formTitle, "Contact Us")}</h2>
              <p className="mb-6 text-base text-muted-foreground">{getString(content.formDescription, "Tell us about your need — Mahirah will respond within 24 hours.")}</p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input id="name" value={form.name} onChange={(e) => { setForm({ ...form, name: e.target.value }); setFieldErrors((prev) => ({ ...prev, name: "" })); }} placeholder="Your full name" className="mt-1" maxLength={100} required disabled={isSubmitting} />
                  {fieldErrors.name && <p className="mt-1 text-sm text-destructive">{fieldErrors.name}</p>}
                </div>
                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input id="email" type="email" value={form.email} onChange={(e) => { setForm({ ...form, email: e.target.value }); setFieldErrors((prev) => ({ ...prev, email: "" })); }} placeholder="you@example.com" className="mt-1" maxLength={255} required disabled={isSubmitting} />
                  {fieldErrors.email && <p className="mt-1 text-sm text-destructive">{fieldErrors.email}</p>}
                </div>
                <div>
                  <Label htmlFor="phone">Phone Number</Label>
                  <div className="mt-1 flex gap-2">
                    <Select value={countryCode} onValueChange={setCountryCode} disabled={isSubmitting}>
                      <SelectTrigger className="w-[120px] shrink-0"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {countryCodes.map((cc) => (
                          <SelectItem key={cc.code} value={cc.code}>{cc.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input id="phone" value={form.phone} onChange={(e) => { const v = e.target.value.replace(/[^0-9\s-]/g, ""); setForm({ ...form, phone: v }); }} placeholder="00000 00000" maxLength={15} disabled={isSubmitting} />
                  </div>
                </div>
                <div>
                  <Label>I'm Interested In *</Label>
                  <Select value={form.subject} onValueChange={(val) => { setForm({ ...form, subject: val }); setFieldErrors((prev) => ({ ...prev, subject: "" })); }} disabled={isSubmitting}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Select a service" /></SelectTrigger>
                    <SelectContent>
                      {subjectOptions.map((option) => (
                        <SelectItem key={option} value={option}>{option}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fieldErrors.subject && <p className="mt-1 text-sm text-destructive">{fieldErrors.subject}</p>}
                </div>
                <div>
                  <Label htmlFor="message">Your Message *</Label>
                  <Textarea id="message" rows={4} value={form.message} onChange={(e) => { setForm({ ...form, message: e.target.value }); setFieldErrors((prev) => ({ ...prev, message: "" })); }} placeholder="Tell us about your goals..." className="mt-1" maxLength={1000} required disabled={isSubmitting} />
                  {fieldErrors.message && <p className="mt-1 text-sm text-destructive">{fieldErrors.message}</p>}
                </div>
                <Button type="submit" size="lg" className="w-full bg-accent font-bold text-accent-foreground hover:bg-accent/90" disabled={isSubmitting}>
                  {isSubmitting ? "Sending..." : getString(content.submitLabel, "Send Message")}
                </Button>
              </form>
            </motion.div>

            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.15 }}>
              <div className="mb-5 rounded-xl bg-primary p-6 text-primary-foreground">
                <h3 className="mb-4 text-xl font-heading font-bold">{getString(content.contactTitle, "Get in Touch")}</h3>
                <div className="space-y-3 text-base">
                  <a href={`tel:${indiaPhone.replace(/\s+/g, "")}`} className="flex items-center gap-2.5 transition-colors hover:text-accent"><Phone className="h-4 w-4 text-accent" /> {indiaPhone}</a>
                  <a href={`tel:${uaePhone.replace(/\s+/g, "")}`} className="flex items-center gap-2.5 transition-colors hover:text-accent"><Phone className="h-4 w-4 text-accent" /> {uaePhone}</a>
                  <a href={`mailto:${email}`} className="flex items-center gap-2.5 transition-colors hover:text-accent"><Mail className="h-4 w-4 text-accent" /> {email}</a>
                </div>

                <h3 className="mb-3 mt-6 text-xl font-heading font-bold">{getString(content.followTitle, "Follow Us")}</h3>
                <div className="space-y-2.5 text-base">
                  <a href={linkedinUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 transition-colors hover:text-accent"><Linkedin className="h-4 w-4 text-accent" /> {getString(content.linkedinLabel, "LinkedIn")}</a>
                  <a href={instagramUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 transition-colors hover:text-accent"><Instagram className="h-4 w-4 text-accent" /> {getString(content.instagramLabel, "@mvibe_on")}</a>
                </div>

                <h3 className="mb-3 mt-6 text-xl font-heading font-bold">{getString(content.visitTitle, "Visit Us")}</h3>
                <div className="flex items-start gap-2.5 text-base">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                  <span>{address}</span>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
                <a href={whatsappIndiaUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 rounded-lg bg-accent px-3 py-2.5 text-base font-semibold text-accent-foreground transition-transform hover:scale-105">
                  <MessageCircle className="h-4 w-4" /> {getString(content.whatsappIndiaLabel, "WhatsApp India")}
                </a>
                <a href={whatsappUaeUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 rounded-lg bg-accent px-3 py-2.5 text-base font-semibold text-accent-foreground transition-transform hover:scale-105">
                  <MessageCircle className="h-4 w-4" /> {getString(content.whatsappUaeLabel, "WhatsApp UAE")}
                </a>
                <a href={`mailto:${email}`} className="flex items-center justify-center gap-2 rounded-lg bg-secondary px-3 py-2.5 text-base font-semibold text-secondary-foreground transition-transform hover:scale-105">
                  <Mail className="h-4 w-4" /> {getString(content.emailButtonLabel, "Email Directly")}
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="bg-muted/30 py-10">
        <div className="container mx-auto px-4">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ duration: 0.6 }} className="mx-auto flex max-w-2xl items-center gap-3 rounded-xl border p-6 bg-card">
            <MapPin className="h-8 w-8 shrink-0 text-accent" />
            <div>
              <h3 className="text-lg font-heading font-bold text-foreground">{getString(content.locationTitle, "Our Location")}</h3>
              <p className="text-base text-muted-foreground">{address}</p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="bg-accent py-8">
        <div className="container mx-auto px-4 text-center">
          <h2 className="mb-3 text-xl font-heading font-bold text-accent-foreground md:text-2xl">{getString(content.quickChatTitle, "Prefer a quick chat? Reach us on WhatsApp for faster response.")}</h2>
          <div className="flex flex-wrap justify-center gap-3">
            <a href={whatsappIndiaUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-base font-bold text-primary-foreground transition-transform hover:scale-105">
              <MessageCircle className="h-4 w-4" /> {getString(content.whatsappIndiaLabel, "WhatsApp India")}
            </a>
            <a href={whatsappUaeUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-base font-bold text-primary-foreground transition-transform hover:scale-105">
              <MessageCircle className="h-4 w-4" /> {getString(content.whatsappUaeLabel, "WhatsApp UAE")}
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
